import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import VideoBlockContainer from './videoBlockContainer';
import { getParticipantBlockStatus, isTrackStreamingStatusActive, setAudienceModeParticipiants, setAudienceTracks } from '../../../utils/functions';

function AudienceMode({ showActiveWindow }) {
    const dominantSpeaker = useSelector(state => state.conf.dominantSpeaker);
    const activeVideoParticipantIds = useSelector(state => state.conf.activeVideoParticipantIds || []);
    const maxVideos = useSelector(state => state.conf.maxVideos || 9);
    const audienceParticipants = useSelector(state => state.conf.audienceParticipants);
    const recentActiveSpeaker = useSelector(state => state.conf.recentActiveSpeaker);
    const audioMode = useSelector(state => state.conf.audioMode);
    const unmutedParticipantIds = useSelector(state => state.conf.unmutedParticipantIds);
    const screenSharedJid = useSelector(state => state.conf.screenSharedJid);
    const localUser = useSelector(state => state.conf.localUser);
    const refreshVideoView = useSelector(state => state.conf.refreshVideoView);
    const currrentAudiencePage = useSelector(state => state.conf.currrentAudiencePage);
    const videoContainerSettings = useSelector(state => state.conf.videoContainerSettings);
    const trackStreamStatus = useSelector(state => state.conf.trackStreamStatus);
    const participantIds = useSelector(state => state.conf.participantIds);
    const screenDimension = useSelector(state => state.conf.screenDimension);
    const currentActiveVideoTracks = useSelector(state => state.conf.currentActiveVideoTracks);
    const extensionDetails = useSelector(state => state.conf.extensionDetails);
    const active_speaker_highlight_color = useSelector(state => state.conf.roomDetail.active_speaker_highlight_color);

    useEffect(() => {
        if (!showActiveWindow) {
            setAudienceModeParticipiants();
        }
    }, [
        dominantSpeaker,
        recentActiveSpeaker,
        showActiveWindow,
        activeVideoParticipantIds,
        unmutedParticipantIds,
        screenSharedJid,
        localUser,
        audioMode,
        refreshVideoView,
        maxVideos,
        currrentAudiencePage,
        videoContainerSettings?.hide_muted_attendees,
    ]);

    useEffect(() => {
        if (!showActiveWindow) {
            let checkOnView = audienceParticipants.filter(filterItem => filterItem.jid !== localUser?.jid).some(item => !participantIds.includes(item.jid));
            if (checkOnView) {
                setAudienceModeParticipiants();
            } else if (audienceParticipants.length < maxVideos) {
                setAudienceModeParticipiants();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participantIds]);


    useEffect(() => {
        console.log('setAudienceTracks', currentActiveVideoTracks);
        if (!showActiveWindow) {
            setAudienceTracks(currentActiveVideoTracks);
        }
    }, [currentActiveVideoTracks, showActiveWindow]);


    // Memoized grid layout class based on maxVideos
    const gridLayoutClass = useMemo(() => {
        if (maxVideos <= 9) { return 'grid-9'; }
        if (maxVideos <= 16) { return 'grid-16'; }
        return 'grid-24';
    }, [maxVideos]);

    // Memoized video size class
    const videoSizeClass = useMemo(() => {
        if (maxVideos <= 9) { return 'video-size-large'; }
        if (maxVideos <= 16) { return 'video-size-medium'; }
        return 'video-size-small';
    }, [maxVideos]);

    const recentUsersDetail = useMemo(() => {
        return audienceParticipants.filter(u => u.isRecentSpeaker);
    }, [audienceParticipants]);

    const recentSpeakerDimensions = useMemo(() => {
        if (!screenDimension) {
            return { width: 0, height: 0, containerWidth: 0 };
        }

        // Always use the same calculation logic regardless of speaker count
        // This ensures consistent box sizes

        const speakerCount = recentUsersDetail.length;
        const { screenWidth, screenHeight } = screenDimension;

        // Calculate available space dynamically
        const aspectRatio = screenWidth / screenHeight;
        const audienceParticipantCount = audienceParticipants.filter(u => !u.isRecentSpeaker).length;

        // Calculate grid layout and aspect ratio based on speaker count
        const getLayoutConfig = (count) => {
            const actualCount = Math.max(count, 1);
            console.log('actualCount', actualCount);
            if (actualCount <= 2) {
                return { rows: 1, cols: actualCount, aspectRatio: 16 / 9 }; // 16:9 rectangles
            } else if (actualCount === 3) {
                return { rows: 1, cols: 3, aspectRatio: 4 / 3 }; // 4:3 rectangles
            } else if (actualCount === 4) {
                return { rows: 1, cols: 4, aspectRatio: 1 }; // squares
            } else if (actualCount <= 9) {
                return { rows: Math.ceil(Math.sqrt(actualCount)), cols: Math.ceil(actualCount / Math.ceil(Math.sqrt(actualCount))), aspectRatio: 16 / 9 };
            } else if (actualCount <= 16) {
                return { rows: 4, cols: 4, aspectRatio: 1 };
            } else if (actualCount <= 24) {
                return { rows: Math.ceil(actualCount / 6), cols: 6, aspectRatio: 4 / 3 };
            }
            return { rows: Math.ceil(actualCount / 8), cols: 8, aspectRatio: 1 };
        };

        const layoutConfig = getLayoutConfig(speakerCount);

        // Calculate available width with side margins (don't fill full width)
        const sideMargins = screenWidth * 0.15; // 15% total side margins (7.5% each side)
        const availableWidthForVideos = screenWidth - sideMargins;

        // Calculate box dimensions based on aspect ratio and available space
        const maxBoxWidth = (availableWidthForVideos - (layoutConfig.cols - 1) * 24) / layoutConfig.cols;

        // Apply different size limits based on speaker count - increased for high resolution
        let maxBoxSizeLimit;
        if (layoutConfig.cols === 1) {
            maxBoxSizeLimit = Math.min(700, screenWidth * 0.4); // Larger for single user
        } else if (layoutConfig.cols === 2) {
            maxBoxSizeLimit = Math.min(600, screenWidth * 0.3); // Medium-large for 2 speakers
        } else if (layoutConfig.cols === 3) {
            maxBoxSizeLimit = Math.min(500, screenWidth * 0.25); // Medium for 3 speakers
        } else if (layoutConfig.cols === 4) {
            maxBoxSizeLimit = Math.min(400, screenWidth * 0.2); // Smaller for 4 speakers
        } else {
            maxBoxSizeLimit = Math.min(300, screenWidth * 0.15); // Default for more speakers
        }

        const boxWidth = Math.min(maxBoxWidth, maxBoxSizeLimit);
        const boxHeight = boxWidth / layoutConfig.aspectRatio;

        // Apply height constraints to ensure boxes fit in available space
        const reservedAudienceHeight = audienceParticipantCount > 0 ? screenHeight * 0.35 : 0;
        const availableHeight = screenHeight - reservedAudienceHeight - 60;
        const maxBoxHeight = availableHeight / layoutConfig.rows - 20; // Account for row gaps

        // Use the smaller of width-based or height-based constraints
        const finalBoxHeight = Math.min(boxHeight, maxBoxHeight);
        const finalBoxWidth = finalBoxHeight * layoutConfig.aspectRatio;

        // Apply minimum size constraints - increased for high resolution
        const minBoxSize = Math.min(screenWidth, screenHeight) * 0.12; // Increased from 0.06 to 0.12 for better visibility
        const constrainedWidth = Math.max(finalBoxWidth, minBoxSize * layoutConfig.aspectRatio);
        const constrainedHeight = Math.max(finalBoxHeight, minBoxSize);

        // Calculate final container width
        const finalContainerWidth = (constrainedWidth * layoutConfig.cols) + ((layoutConfig.cols - 1) * 24);

        return {
            width: constrainedWidth,
            height: constrainedHeight,
            containerWidth: finalContainerWidth,
            rows: layoutConfig.rows,
            cols: layoutConfig.cols,
            aspectRatio: layoutConfig.aspectRatio,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recentUsersDetail.length, screenDimension]);

    const _participantsList = useMemo(() => {
        return audienceParticipants.filter(u => !u.isDominantSpeaker && !u.isRecentSpeaker);
    }, [audienceParticipants]);

    // Calculate dynamic circle size and grid layout based on screen dimensions and participant count
    const gridLayout = useMemo(() => {
        if (!screenDimension) {
            return { circleSize: 120, columns: 1, rows: 1, gapSize: 20, activeSpeakerHeight: 400 };
        }

        // If no audience participants, use full screen for recent speakers in gallery style
        if (_participantsList.length === 0) {
            return { circleSize: 120, columns: 1, rows: 1, gapSize: 20, activeSpeakerHeight: screenDimension.screenHeight };
        }

        const { screenWidth, screenHeight } = screenDimension;

        const participantCount = _participantsList.length;

        // Constants for cleaner code
        const BASE_ACTIVE_SPEAKER_RATIO = 0.45;
        const MIN_GAP = 40;
        const MAX_GAP = 100;
        const MARGIN = 60;

        // Calculate extra vertical space and distribute it
        const minRequiredHeight = (screenHeight * BASE_ACTIVE_SPEAKER_RATIO) + (screenHeight * 0.3) + MIN_GAP;
        const extraVerticalSpace = Math.max(0, screenHeight - minRequiredHeight);

        const activeSpeakerBonus = extraVerticalSpace * 0.6;
        const audienceBonus = extraVerticalSpace * 0.4;

        const activeSpeakerHeight = (screenHeight * BASE_ACTIVE_SPEAKER_RATIO) + activeSpeakerBonus;
        const baseAudienceHeight = screenHeight - activeSpeakerHeight;
        const dynamicGap = Math.min(MAX_GAP, MIN_GAP + (extraVerticalSpace * 0.1));

        const availableHeight = baseAudienceHeight - dynamicGap + audienceBonus;
        const availableWidth = screenWidth - MARGIN;

        // Helper function to calculate optimal layout
        const calculateOptimalLayout = (minSize, maxSingleRowCount) => {
            const gapSize = Math.max(8, Math.min(20, screenWidth * 0.01));
            const aspectRatio = screenWidth / screenHeight;

            // Try single row first if participant count allows
            if (participantCount <= maxSingleRowCount) {
                const requiredWidth = (participantCount * minSize) + ((participantCount - 1) * gapSize);
                if (requiredWidth <= availableWidth) {
                    return { columns: participantCount, rows: 1 };
                }
            }

            // Calculate optimal columns based on available width
            const maxColumns = Math.floor((availableWidth + gapSize) / (minSize + gapSize));
            let columns = Math.max(1, Math.min(maxColumns, participantCount));

            // For 16-view, ensure balanced layout between 1-3 rows
            if (maxVideos <= 16) {
                const maxDesirableColumns = Math.min(8, columns); // Limit to max 8 per row for 16-view

                // Prefer 2 rows for 9+ participants, avoid single row overcrowding
                if (participantCount >= 9) {
                    const idealColumns = Math.ceil(participantCount / 2); // Target 2 rows
                    columns = Math.min(columns, Math.max(idealColumns, 6)); // At least 6 columns, but not more than needed for 2 rows
                } else {
                    columns = Math.min(columns, maxDesirableColumns);
                }
            } else {
                // Apply normal aspect ratio restrictions for other views
                if (aspectRatio > 1.8) { columns = Math.min(columns, Math.ceil(participantCount / 2)); }
                else if (aspectRatio > 1.3) { columns = Math.min(columns, Math.ceil(participantCount / 2.5)); }
            }

            return { columns, rows: Math.ceil(participantCount / columns) };
        };

        // Determine layout based on maxVideos
        let columns, rows;
        if (maxVideos <= 9) {
            // Check if we should use 2-row layout for better space utilization
            const singleRowWidth = (availableWidth - (participantCount - 1) * Math.max(8, Math.min(20, screenWidth * 0.01))) / participantCount;
            const isWidthConstrained = singleRowWidth < 120; // If circles would be smaller than 120px
            const hasVerticalSpace = availableHeight > 300; // If we have good vertical space

            console.log('Audience Grid Debug:', {
                participantCount,
                availableHeight,
                availableWidth,
                singleRowWidth,
                isWidthConstrained,
                hasVerticalSpace,
                maxVideos,
            });

            if ((isWidthConstrained || hasVerticalSpace) && participantCount > 6) {
                // Force 2-row layout for better circle sizes
                const colsPerRow = Math.ceil(participantCount / 2);
                columns = colsPerRow;
                rows = 2;
                console.log('Using 2-row audience layout:', { columns, rows });
            } else {
                ({ columns, rows } = calculateOptimalLayout(80, 9));
            }
        } else if (maxVideos <= 16) {
            // Check if we should use 2-row layout for 16-view (5-6 columns per row)
            const singleRowWidth16 = (availableWidth - (participantCount - 1) * Math.max(2, Math.min(8, screenWidth * 0.005))) / participantCount;
            const isWidthConstrained16 = singleRowWidth16 < 80; // If circles would be smaller than 80px
            const hasVerticalSpace16 = availableHeight > 250; // If we have good vertical space

            // Check for horizontal space availability for 2-row layout
            const estimatedCircleSize = Math.min(singleRowWidth16, 120); // Estimated circle size
            const maxColsPerRow = Math.floor((availableWidth + 8) / (estimatedCircleSize + 8)); // Max columns that fit
            const hasHorizontalSpaceFor2Rows = maxColsPerRow >= 5; // Can fit at least 5 columns

            if ((isWidthConstrained16 || hasVerticalSpace16 || hasHorizontalSpaceFor2Rows) && participantCount > 8) {
                // Dynamic 2-row layout based on participant count
                let firstRowCount, secondRowCount;

                if (participantCount <= 10) {
                    // For 8-10 participants: distribute evenly (5-5 or 6-4)
                    firstRowCount = Math.ceil(participantCount / 2);
                    secondRowCount = participantCount - firstRowCount;
                } else if (participantCount <= 12) {
                    // For 11-12 participants: 7-5 or 8-4
                    firstRowCount = Math.ceil(participantCount * 0.6);
                    secondRowCount = participantCount - firstRowCount;
                } else if (participantCount <= 14) {
                    // For 13-14 participants: 8-6 or 9-5
                    firstRowCount = Math.ceil(participantCount * 0.65);
                    secondRowCount = participantCount - firstRowCount;
                } else {
                    // For 15-16 participants: 10-6 layout
                    firstRowCount = 10;
                    secondRowCount = participantCount - firstRowCount;
                }

                columns = firstRowCount;
                rows = 2;
                console.log('Using dynamic 2-row audience layout for 16-view:', {
                    columns,
                    rows,
                    participantCount,
                    layout: `${firstRowCount}-${secondRowCount}`,
                });
            } else {
                ({ columns, rows } = calculateOptimalLayout(70, 8));
            }
        } else {
            ({ columns, rows } = calculateOptimalLayout(60, 12));
        }

        // Calculate final dimensions - use different gap sizes based on view type
        let gapSize;
        if (maxVideos <= 9) {
            gapSize = Math.max(2, Math.min(8, screenWidth * 0.005));
        } else if (maxVideos <= 16) {
            if (rows === 2) {
                gapSize = Math.max(1, Math.min(6, screenWidth * 0.003));
            } else {
                // Check if we have abundant horizontal space for single row 16-view
                const totalCircleWidth = participantCount * 70; // Estimated circle size
                const totalGapWidth = (participantCount - 1) * Math.max(4, Math.min(10, screenWidth * 0.007));
                const requiredWidth = totalCircleWidth + totalGapWidth;
                const hasHorizontalSpace = availableWidth > requiredWidth * 1.2; // 20% extra space available

                console.log('16-view horizontal space debug:', {
                    participantCount,
                    availableWidth,
                    totalCircleWidth,
                    totalGapWidth,
                    requiredWidth,
                    hasHorizontalSpace,
                    threshold: requiredWidth * 1.2,
                });

                gapSize = hasHorizontalSpace ? Math.max(12, Math.min(20, screenWidth * 0.012)) : Math.max(4, Math.min(10, screenWidth * 0.007));
            }
        } else {
            gapSize = Math.max(6, Math.min(12, screenWidth * 0.008));
        }
        console.log('gapSize', gapSize, 'maxVideos', maxVideos, 'rows', rows);
        const maxWidthPerCircle = (availableWidth - (columns - 1) * gapSize) / columns;
        const maxHeightPerCircle = (availableHeight - (rows - 1) * gapSize) / rows;
        const calculatedSize = Math.min(maxWidthPerCircle, maxHeightPerCircle);

        // Apply size constraints with vertical space bonus - consistent sizing based on participant count, not view mode
        let baseMinSize, baseMaxSize;

        // Size based on actual participant count, not maxVideos setting - increased for high resolution
        if (participantCount <= 1) {
            // Single participant - increased size for high resolution
            baseMinSize = Math.max(120, screenWidth * 0.08);
            baseMaxSize = Math.min(400, screenWidth * 0.25);
        } else if (participantCount <= 4) {
            // Small group - increased size
            baseMinSize = Math.max(100, screenWidth * 0.07);
            baseMaxSize = Math.min(350, screenWidth * 0.22);
        } else if (participantCount <= 9) {
            // Medium group - increased size
            baseMinSize = Math.max(90, screenWidth * 0.06);
            baseMaxSize = Math.min(300, screenWidth * 0.20);
        } else if (participantCount <= 16) {
            // Large group - increased size
            baseMinSize = Math.max(80, screenWidth * 0.05);
            baseMaxSize = Math.min(250, screenWidth * 0.18);
        } else {
            // Very large group - increased minimum size
            baseMinSize = Math.max(70, screenWidth * 0.04);
            baseMaxSize = Math.min(200, screenWidth * 0.15);
        }
        console.log('baseMinSize', baseMinSize, baseMaxSize)

        const verticalSpaceBonus = Math.min(50, extraVerticalSpace * 0.1);
        const enhancedMaxSize = baseMaxSize + verticalSpaceBonus;

        const circleSize = Math.max(baseMinSize, Math.min(enhancedMaxSize, calculatedSize));

        // Calculate if last row is incomplete for centering
        const isLastRowIncomplete = participantCount % columns !== 0;
        const lastRowItemCount = participantCount % columns;

        return { circleSize, columns, rows, gapSize, activeSpeakerHeight, isLastRowIncomplete, lastRowItemCount };
    }, [screenDimension, _participantsList.length, maxVideos]);


    return (
        <>
            {
                audienceParticipants.length > 0 ? (
                    <div className="audience-mode-container">
                        {/* Recent Speakers Grid Layout */}
                        {(
                            <div className="recent-speakers-container" style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${recentSpeakerDimensions.cols || 1}, 1fr)`,
                                gridTemplateRows: `repeat(${recentSpeakerDimensions.rows || 1}, 1fr)`,
                                gap: '24px 24px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                justifyItems: 'center',
                                marginBottom: '30px',
                                width: 'fit-content',
                                maxWidth: recentSpeakerDimensions.containerWidth,
                                minHeight: recentSpeakerDimensions.height * (recentSpeakerDimensions.rows || 1) + Math.max(0, (recentSpeakerDimensions.rows - 1)) * 20,
                                position: 'relative',
                                marginTop: '.5vh',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                backgroundColor: 'transparent',
                                overflow: 'visible',
                            }}>
                                {/* Always render grid slots to maintain consistent positioning */}
                                {Array.from({ length: Math.max(recentUsersDetail.length, 1) }, (_, index) => {
                                    const participant = recentUsersDetail[index];
                                    const  speakerClass =  participant?.jid === dominantSpeaker && recentUsersDetail.length > 1 ? 'highlighted-box'  : '';
                                    let isAttendeeRecorder = participant?.isAttendeeRecorder;
                                    return (
                                        <div
                                            key={participant?.jid || `placeholder-${index}`}
                                            className={`recent-speaker-video ${speakerClass} ${recentUsersDetail.length === 2 ? 'aspect-contain' :
                                                    Math.abs(recentSpeakerDimensions.aspectRatio - (16 / 9)) < 0.1 ? 'aspect-16-9' :
                                                        Math.abs(recentSpeakerDimensions.aspectRatio - (4 / 3)) < 0.1 ? 'aspect-4-3' :
                                                            Math.abs(recentSpeakerDimensions.aspectRatio - 1) < 0.1 ? 'aspect-square' : 'aspect-other'
                                                }`}
                                            style={{
                                                width:
                                                recentUsersDetail.length === 1 &&
                                                _participantsList.length === 0
                                                  ? screenDimension.singleUserViewWidth
                                                  : recentSpeakerDimensions.width,
                                              height:
                                                recentUsersDetail.length === 1 &&
                                                _participantsList.length === 0
                                                  ? screenDimension.singleUserViewHeight
                                                  : recentSpeakerDimensions.height,
                                                position: 'relative',
                                                flexShrink: 0,
                                                overflow: 'visible',
                                                '--dynamic-shadow': `inset 0 0 0 .2em ${active_speaker_highlight_color}`,
                                            }}>
                                            {participant ? (
                                                <VideoBlockContainer
                                                    participant={participant}
                                                    isVideoInactive={!isTrackStreamingStatusActive(participant?.track, trackStreamStatus)}
                                                    isBlocked={getParticipantBlockStatus(participant)}
                                                    isAttendeeRecorder={isAttendeeRecorder}
                                                // isCircular={true}
                                                />
                                            ) : (
                                                <div className="videoBlockInner ">
                                                    <div>
                                                        When someone speaks, they will appear here.
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Audience Grid - Only show if there are participants to display */}
                        {_participantsList.length > 0 && (
                            <div
                                className={`audience-grid ${gridLayoutClass}`}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: `${gridLayout.gapSize}px`,
                                    width: '100%',
                                    margin: '0 auto',
                                }}>
                                {Array.from({ length: gridLayout.rows }, (_, rowIndex) => {
                                    const startIndex = rowIndex * gridLayout.columns;
                                    const endIndex = Math.min(startIndex + gridLayout.columns, _participantsList.length);
                                    const rowParticipants = _participantsList.slice(startIndex, endIndex);
                                    const isLastRow = rowIndex === gridLayout.rows - 1;
                                    const isIncompleteRow = rowParticipants.length < gridLayout.columns;

                                    return (
                                        <div
                                            key={`row-${rowIndex}`}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                gap: `${gridLayout.gapSize}px`,
                                                width: '100%',
                                            }}>
                                            {rowParticipants.map((participant, participantIndex) => (
                                                <div
                                                    key={`audience-${participant.jid}`}
                                                    className={`audience-video-container ${videoSizeClass}`}
                                                    style={{
                                                        width: `${gridLayout.circleSize}px`,
                                                        height: `${gridLayout.circleSize}px`,
                                                        minWidth: `${gridLayout.circleSize}px`,
                                                        minHeight: `${gridLayout.circleSize}px`,
                                                        '--circle-size': `${gridLayout.circleSize}px`,
                                                        background: participant?.isAttendeeRecorder && extensionDetails?.backgroundColor,
                                                        color: participant?.isAttendeeRecorder && extensionDetails?.textColor,
                                                    }}>
                                                    <VideoBlockContainer
                                                        participant={participant}
                                                        isVideoInactive={!isTrackStreamingStatusActive(participant?.track, trackStreamStatus)}
                                                        isBlocked={getParticipantBlockStatus(participant)}
                                                        isCircular={true}
                                                        isAttendeeRecorder={participant?.isAttendeeRecorder}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <span className="text-black stand-by">
                        Please stand by until someone is unmuted.
                    </span>
                )
            }
        </>
    );
}

export default React.memo(AudienceMode);
